/**
 * Leave Ritual Confirmation Modal
 * Modal to confirm leaving a ritual, following LogoutConfirmationModal pattern
 */

import { LogOut, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"

interface LeaveRitualModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
  ritualTitle?: string
  isDelete?: boolean
}

export function LeaveRitualModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  ritualTitle,
  isDelete = false,
}: LeaveRitualModalProps) {
  const { t } = useTranslation("chats")

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent showCloseButton={false} className="w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red/10">
            {isDelete ? (
              <Trash2 size={24} className="text-red" />
            ) : (
              <LogOut size={24} className="text-red" />
            )}
          </div>
        </div>

        <DialogHeader className="text-center">
          <DialogTitle className="text-charcoal text-2xl font-semibold text-center">
            {isDelete ? t("deleteRitualModal.title") : t("leaveRitualModal.title")}
          </DialogTitle>
          <DialogDescription className="text-stone text-center text-sm leading-relaxed">
            {isDelete
              ? t("deleteRitualModal.description", { title: ritualTitle })
              : ritualTitle
                ? t("leaveRitualModal.descriptionWithTitle", { title: ritualTitle })
                : t("leaveRitualModal.description")}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-row gap-3 sm:flex-row">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 rounded-lg border-gray text-charcoal"
          >
            {isDelete ? t("deleteRitualModal.cancel") : t("leaveRitualModal.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 rounded-lg bg-red"
          >
            {isLoading
              ? isDelete
                ? t("deleteRitualModal.deleting")
                : t("leaveRitualModal.leaving")
              : isDelete
                ? t("deleteRitualModal.delete")
                : t("leaveRitualModal.leave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
